import { AppErrorCode } from '@/core/errors/appErrorCode';

export type ResolvedError = {
  code: AppErrorCode;
  message: string;
};

/**
 * Maps API / axios errors to a stable code + user-facing message.
 * TODO: align payload shape with backend AppError responses.
 */
export function resolveError(err: unknown): ResolvedError {
  if (err && typeof err === 'object' && 'response' in err) {
    const ax = err as { response?: { status?: number; data?: { message?: string; code?: string } } };
    const status = ax.response?.status;
    const data = ax.response?.data;
    const simultaneousLimitMessage = 'Todas as licenças de usuários simultâneos estão em uso. Aguarde um usuário sair ou contrate mais licenças.';
    const licensingMessages: Partial<Record<AppErrorCode, string>> = {
      [AppErrorCode.PLAN_CONFIGURATION_CONFLICT]: 'O plano, preço ou vínculo mudou. Atualize os dados e revise a alteração novamente.',
      [AppErrorCode.PLAN_UNAVAILABLE]: 'Selecione um plano ativo, com módulos e preço configurados na moeda escolhida.',
      [AppErrorCode.PLAN_REQUIRED]: 'Esta empresa precisa de um plano vigente para acessar o sistema.',
      [AppErrorCode.PLAN_MODULE_DISABLED]: 'O plano selecionado não inclui um recurso em uso pela empresa (ex.: canal WhatsApp Oficial, Instagram ou Messenger). Exclua o canal ou escolha outro plano.',
      [AppErrorCode.LICENSE_REDUCTION_BELOW_USAGE]: 'A capacidade de canais contratada não pode ficar abaixo dos canais em uso.',
      [AppErrorCode.QUOTA_WHATSAPP_CONNECTIONS_EXCEEDED]: 'Todos os canais contratados estão em uso. Exclua um canal ou contrate mais capacidade.',
      // O bloqueio vigente é CONCURRENT_USERS_LIMIT_REACHED; QUOTA_USERS_EXCEEDED fica como fallback de backends antigos.
      [AppErrorCode.CONCURRENT_USERS_LIMIT_REACHED]: simultaneousLimitMessage,
      [AppErrorCode.QUOTA_USERS_EXCEEDED]: simultaneousLimitMessage,
    };
    const code = data?.code as AppErrorCode | undefined;
    if (code && licensingMessages[code]) return { code, message: licensingMessages[code]! };
    if (data?.code === 'CONNECTOR_OPERATION_TIMEOUT') {
      return { code: AppErrorCode.CONNECTOR_OPERATION_TIMEOUT, message: 'A operação demorou mais que o esperado. Os dados foram preservados. Tente novamente em instantes.' };
    }
    if (status === 401) {
      return { code: AppErrorCode.UNAUTHORIZED, message: data?.message || 'Sessão expirada. Faça login novamente.' };
    }
    if (status === 403) {
      return { code: AppErrorCode.FORBIDDEN, message: data?.message || 'Você não tem permissão para esta ação.' };
    }
    if (status === 404) {
      return { code: AppErrorCode.NOT_FOUND, message: data?.message || 'Recurso não encontrado.' };
    }
    if (data?.message) {
      return { code: AppErrorCode.UNKNOWN, message: data.message };
    }
  }
  if (err instanceof Error) {
    return { code: AppErrorCode.UNKNOWN, message: err.message };
  }
  return { code: AppErrorCode.INTERNAL_ERROR, message: 'Algo deu errado. Tente novamente.' };
}
